import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { paymentService } from "../services/paymentService";
import { courseService } from "../services/courseService";
import Logo from "../asset/images/home/Logo (1).png";
import LeftImage from "../asset/images/home/Rectangle 2756.png";

function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        try {
            const result = await login(email, password);
            
            if (result.success) {
                const returnUrl = searchParams.get("returnUrl");
                const user = result.user;
                
                const isAdminCreatedUser = user?.schoolId !== null && user?.schoolId !== undefined;
                
                let hasCompletedPayment = false;
                let hasSchoolBulkEnrollment = false;
                
                try {
                    const coursesResponse = await courseService.getCourses();
                    const courses = coursesResponse.data?.courses || [];
                    
                    if (courses.length > 0) {
                        const mainCourse = courses[0];
                        
                        const paymentHistoryResponse = await paymentService.getPaymentHistory();
                        const payments = paymentHistoryResponse.data?.payments || [];
                        
                        hasCompletedPayment = payments.some(
                            payment => 
                                payment.courseId === mainCourse.id && 
                                payment.status === 'COMPLETED'
                        );
                        
                        if (isAdminCreatedUser) {
                            hasSchoolBulkEnrollment = true;
                        } else if (user?.enrollments && Array.isArray(user.enrollments)) {
                            hasSchoolBulkEnrollment = user.enrollments.some(
                                enrollment => 
                                    enrollment.courseId === mainCourse.id && 
                                    enrollment.purchaseType === 'SCHOOL_BULK'
                            );
                        }
                    }
                } catch (paymentCheckError) {
                    console.error("Error checking payment status:", paymentCheckError);
                }
                
                const shouldSkipPayment = hasCompletedPayment || hasSchoolBulkEnrollment || isAdminCreatedUser;
                
                
                if (returnUrl && returnUrl.includes('/register/payment-detail')) {
                    if (shouldSkipPayment) {
                        navigate("/dashboard-home");
                        return;
                    } else {
                        
                        navigate(returnUrl);
                        return;
                    }
                }
                
                
                if (returnUrl) {
                    navigate(returnUrl);
                    return;
                }
                
                
                if (!shouldSkipPayment) {
                    navigate("/register/payment-card");
                    return;
                }
                
                navigate("/dashboard-home");
            } else {
                setError(result.error || "Login failed. Please try again.");
            }
        } catch (err) {
            setError("An unexpected error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col lg:flex-row h-screen">
            <div className="hidden lg:block w-7/12 h-full">
                <img
                    src={LeftImage}
                    alt="READ 21 Science of Reading"
                    className="w-full h-full object-cover"
                />
            </div>

            {/* Right Side (Form Section) */}
            <div className="flex flex-col justify-between w-full lg:w-5/12 bg-white">
                {/* Logo */}
                <div className="flex justify-center mt-12">
                    <img
                        src={Logo}
                        alt="READ 21 Logo"
                        className="w-auto"
                    />
                </div>

                {/* Login Card */}
                <div className="flex justify-center items-center flex-grow px-2">
                    <div className="bg-white shadow-lg rounded-xl p-8 w-full max-w-md border border-gray-100">
                        <h1 className="text-3xl font-bold text-primary mb-6">Log in</h1>

                        {/* Error Message */}
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                                <p className="text-sm text-red-600">{error}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            {/* Email Field */}
                            <div className="relative mb-3">
                                <span className="absolute left-3 top-3 text-gray-400">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                                    </svg>
                                </span>
                                <input
                                    type="email"
                                    placeholder="Enter Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                    style={{ lineHeight: "1.4" }}
                                />
                            </div>

                            {/* Password Field */}
                            <div className="relative mb-3">
                                <span className="absolute left-3 top-3 text-gray-400">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                                    </svg>
                                </span>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Enter Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                    style={{ lineHeight: "1.4" }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {showPassword ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                        )}
                                    </svg>
                                </button>
                            </div>

                            {/* Forgot Password */}
                            <Link to="/forgot-password/send-mail">
                                <div className="flex justify-end mb-6 text-sm text-primary hover:underline cursor-pointer">
                                    <p>Forgot Password</p>
                                </div>
                            </Link>

                            {/* Login Button */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-primary hover:bg-primaryDark text-white font-semibold py-3 rounded-md transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? "Logging in..." : "Login"}
                            </button>
                        </form>

                        {/* Register Link */}
                        <div className="mt-6 text-center">
                            <p className="text-sm text-gray-600">
                                Don't have an account?{" "}
                                <Link to="/register" className="text-primary hover:underline font-medium">
                                    Register here
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center text-gray-500 text-sm mb-4 mt-4">
                    Read 21 © 2025 - All Rights Reserved
                </div>
            </div>
        </div>
    );
}

export default Login;
